import { useSubscription as useApolloSubscription } from "@apollo/client";
import { orderBy } from "lodash";
import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import ActivitiesLog from "shared/components/ActivitiesLog";
import Loading from "shared/components/Spin";
import { useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { QUALIFIERS } from "utils/api/graphql/queries/qualifiers";
import { QUOTES } from "utils/api/graphql/queries/quotes";
import { SUBSCRIPTIONS } from "utils/api/graphql/queries/subscription";
import { CALLS } from "utils/api/graphql/queries/call";
import graphql from "utils/api/graphql";
import {
  ACTIVITY_FILTERS,
  ACTIVITY_TYPES,
  SUBSCRIPTION_STATUSES,
  TYPES,
} from "utils/constants";

const CALLS_PAGE_SIZE = 10;

const SubscriptionActivities = ({ project, contactId, phoneNumber }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTACT);
  const [activeFilter, setActiveFilter] = useState(ACTIVITY_FILTERS.ALL);
  const [callsDisplayCount, setCallsDisplayCount] = useState(CALLS_PAGE_SIZE);

  const filters = [
    { key: ACTIVITY_FILTERS.ALL, label: t("ACTIVITIES.FILTERS.ALL") },
    { key: ACTIVITY_FILTERS.CALLS, label: t("ACTIVITIES.FILTERS.CALLS") },
    {
      key: ACTIVITY_FILTERS.QUALIFICATIONS,
      label: t("ACTIVITIES.FILTERS.QUALIFICATIONS"),
    },
    {
      key: ACTIVITY_FILTERS.STATUSES,
      label: t("ACTIVITIES.FILTERS.STATUSES"),
    },
  ];

  const variables = {
    where: {
      project: {
        id: project?.id,
      },
    },
  };

  const { data: qualifiersData, loading: qualifiersLoading } = useQuery(
    QUALIFIERS,
    {
      variables: {
        withDeleted: true,
        where: {
          qualifications: {
            contact: { id: contactId },
          },
        },
      },
    },
  );

  const { loading, data } = useQuery(SUBSCRIPTIONS, {
    variables: {
      where: {
        AND: [
          {
            project: {
              id: project?.id,
            },
          },
          {
            OR: [
              { status: SUBSCRIPTION_STATUSES.PENDING },
              { status: SUBSCRIPTION_STATUSES.SIGNED },
            ],
          },
        ],
      },
    },
  });

  const {
    loading: { quotesLoading },
    data: quotesData,
  } = useQuery(QUOTES, {
    variables,
  });

  const callsWhere = phoneNumber
    ? { phoneNumber }
    : { contact: { id: contactId } };

  const {
    data: callsData,
    loading: callsLoading,
    refetch: refetchCalls,
  } = useQuery(CALLS, {
    variables: {
      where: callsWhere,
      orderBy: [{ updatedDate: "DESC" }],
      take: callsDisplayCount,
    },
    skip: !phoneNumber && !contactId,
  });

  const subscriptionWhere = phoneNumber
    ? { phoneNumber }
    : { contact: { id: contactId } };

  useApolloSubscription(graphql.subscriptions.CALL_ADDED, {
    variables: { where: subscriptionWhere },
    skip: !phoneNumber && !contactId,
    onData: () => refetchCalls(),
  });

  if (loading || quotesLoading || qualifiersLoading || callsLoading) {
    return <Loading />;
  }

  const subscriptions = data?.subscriptions?.data || [];
  const quotes = quotesData?.quotes?.data || [];
  const qualifiers = qualifiersData?.qualifiers?.data || [];
  const calls = callsData?.calls?.data || [];
  const callsTotalCount = callsData?.calls?.count || 0;
  const hasMoreCalls = calls.length < callsTotalCount;

  const qualificationActivities = qualifiers.flatMap(
    ({ qualifications, name }) =>
      qualifications.map(({ createdDate }) => ({
        activityType: ACTIVITY_TYPES.QUALIFICATION,
        status: name,
        date: createdDate,
        author: project?.author,
      })),
  );

  const statusActivities = [
    ...quotes.flatMap(({ createdDate }) => ({
      activityType: ACTIVITY_TYPES.STATUS,
      status: SUBSCRIPTION_STATUSES.DRAFT,
      date: createdDate,
      type: TYPES.DANGER,
      author: project?.author,
    })),
    ...subscriptions.flatMap(({ sendSignatureDate, signedDate, status }) =>
      status === SUBSCRIPTION_STATUSES.PENDING
        ? {
            activityType: ACTIVITY_TYPES.STATUS,
            date: sendSignatureDate,
            status: SUBSCRIPTION_STATUSES.PENDING,
            type: TYPES.DANGER,
            author: project?.author,
          }
        : [
            {
              activityType: ACTIVITY_TYPES.STATUS,
              date: sendSignatureDate,
              status: SUBSCRIPTION_STATUSES.PENDING,
              type: TYPES.DANGER,
              author: project?.author,
            },
            {
              activityType: ACTIVITY_TYPES.STATUS,
              date: signedDate,
              status: SUBSCRIPTION_STATUSES.SIGNED,
              type: TYPES.DANGER,
              author: project?.author,
            },
          ],
    ),
  ];

  const callActivities = calls.map((call) => ({
    activityType: ACTIVITY_TYPES.CALL,
    date: call.updatedDate || call.startedAt || call.dialedAt,
    callData: call,
  }));

  let filtered = [];
  if (activeFilter === ACTIVITY_FILTERS.ALL) {
    filtered = [
      ...qualificationActivities,
      ...statusActivities,
      ...callActivities,
    ];
  } else if (activeFilter === ACTIVITY_FILTERS.CALLS) {
    filtered = callActivities;
  } else if (activeFilter === ACTIVITY_FILTERS.QUALIFICATIONS) {
    filtered = qualificationActivities;
  } else if (activeFilter === ACTIVITY_FILTERS.STATUSES) {
    filtered = statusActivities;
  }

  const activities = orderBy(
    filtered,
    (activity) => {
      const m = moment(activity.date);
      return m.isValid() ? m.valueOf() : 0;
    },
    "desc",
  );

  const handleLoadMore = () => {
    setCallsDisplayCount((prev) => prev + CALLS_PAGE_SIZE);
  };

  return (
    <div className="activities-tab">
      <div className="activities-tab__filters">
        {filters.map(({ key, label }) => (
          <button
            key={key}
            className={classNames("activities-tab__filter", {
              "activities-tab__filter--active": activeFilter === key,
            })}
            onClick={() => setActiveFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>
      {!activities.length ? (
        <div className="no-activity">{t("ACTIVITIES.NO_ACTIVITY")}</div>
      ) : (
        <ActivitiesLog
          activities={activities}
          hasMore={hasMoreCalls}
          onLoadMore={handleLoadMore}
        />
      )}
    </div>
  );
};

export default SubscriptionActivities;
